import React from 'react'
import PropTypes from 'prop-types'
import { Controller, useFormContext } from 'react-hook-form'
import TextField from '@material-ui/core/TextField'
import IconButton from '@material-ui/core/IconButton'
import InputAdornment from '@material-ui/core/InputAdornment'
import Autocomplete from '@material-ui/lab/Autocomplete'
import Box from '@material-ui/core/Box'
import CloseIcon from 'components/material-symbol/icons/Close'
import FacilityAlgoliaSettingsField, {
  formatFacilityDisplayLabel,
} from 'components/facilities/facility-algolia-settings-field'
import { CtrlTextField } from 'components/form-util'
import usStates from 'utils/usStates'
import timezones from 'utils/timezones'

const FacilityInformationFields = ({
  classes,
  control,
  register,
  errors,
  facility,
  facilityPersisted,
  preventExternalFacilityEdit,
  setFacility,
  clearSelectedFacility,
}) => {
  const { watch } = useFormContext()
  const externalFacilityId = watch('facility.externalFacilityId')
  const city = watch('facility.city')
  const state = watch('facility.state')

  return (
    <>
      <Controller
        name="facility.name"
        control={control}
        render={({ name, value, ref, onChange, onBlur }) => {
          if (preventExternalFacilityEdit) {
            const displayValue = externalFacilityId
              ? formatFacilityDisplayLabel(value, city, state)
              : value
            const canClearSelection = !facilityPersisted && externalFacilityId

            return (
              <TextField
                required
                name={name}
                data-cy="facility.name"
                label="Facility Name"
                variant="outlined"
                className={classes.facilityInput}
                error={!!errors.facility?.name}
                helperText={errors.facility?.name?.message}
                inputRef={ref}
                value={displayValue}
                autoComplete="off"
                disabled={!canClearSelection}
                fullWidth
                InputProps={
                  canClearSelection
                    ? {
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={clearSelectedFacility}
                            edge="end"
                            aria-label="Clear selection"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }
                    : undefined
                }
              />
            )
          }

          if (facilityPersisted) {
            return (
              <TextField
                required
                name={name}
                data-cy="facility.name"
                label="Facility Name"
                variant="outlined"
                className={classes.facilityInput}
                error={!!errors.facility?.name}
                helperText={errors.facility?.name?.message}
                inputRef={ref}
                value={value}
                onChange={onChange}
                autoComplete="off"
                fullWidth
              />
            )
          }

          return (
            <FacilityAlgoliaSettingsField
              name={name}
              onSelect={setFacility}
              onClear={clearSelectedFacility}
              value={value}
              onBlur={onBlur}
              error={!!errors.facility?.name}
              helperText={errors.facility?.name?.message}
              onChange={onChange}
              externalFacilityId={externalFacilityId}
              city={city}
              state={state}
            />
          )
        }}
      />
      <input name="facility.externalFacilityId" type="hidden" ref={register} />
      <Controller
        name="facility.address1"
        control={control}
        render={({ name, value, ref, onChange }) => (
          <TextField
            required
            name={name}
            data-cy="facility.address1"
            label="Address"
            variant="outlined"
            className={classes.facilityInput}
            error={!!errors.facility?.address1}
            helperText={errors.facility?.address1?.message}
            inputRef={ref}
            disabled={preventExternalFacilityEdit}
            value={value}
            onChange={onChange}
            fullWidth
          />
        )}
      />
      <Controller
        name="facility.address2"
        control={control}
        render={({ name, value, ref, onChange }) => (
          <TextField
            name={name}
            data-cy="facility.address2"
            label="Address 2"
            variant="outlined"
            className={classes.facilityInput}
            error={!!errors.facility?.address2}
            helperText={errors.facility?.address2?.message}
            inputRef={ref}
            disabled={preventExternalFacilityEdit}
            value={value}
            onChange={onChange}
            fullWidth
          />
        )}
      />
      <Box display="flex" flexWrap="wrap" style={{ gap: 16 }}>
        <Box flex="1 1 160px" minWidth={0}>
          <Controller
            name="facility.city"
            control={control}
            render={({ name, value, ref, onChange }) => (
              <TextField
                required
                name={name}
                data-cy="facility.city"
                label="City"
                variant="outlined"
                className={classes.facilityInput}
                error={!!errors.facility?.city}
                helperText={errors.facility?.city?.message}
                inputRef={ref}
                disabled={preventExternalFacilityEdit}
                value={value}
                onChange={onChange}
                fullWidth
              />
            )}
          />
        </Box>
        <Box flex="1 1 120px" minWidth={0}>
          <Controller
            name="facility.state"
            control={control}
            render={({ name, value, ref, onChange }) => (
              <Autocomplete
                name={name}
                data-cy="facility.state"
                disableClearable
                required
                value={value}
                options={usStates}
                onChange={(e, nextValue) => onChange(nextValue)}
                inputRef={ref}
                disabled={preventExternalFacilityEdit}
                className={classes.facilityInput}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="State"
                    variant="outlined"
                    error={!!errors.facility?.state}
                    helperText={errors.facility?.state?.message}
                  />
                )}
              />
            )}
          />
        </Box>
        <Box flex="1 1 100px" minWidth={0}>
          <CtrlTextField
            name="facility.zip"
            data-cy="facility.zip"
            control={control}
            errors={errors}
            disabled={preventExternalFacilityEdit}
            label="Zip"
            required
            variant="outlined"
            className={classes.facilityInput}
            fullWidth
          />
        </Box>
      </Box>
      {!preventExternalFacilityEdit && (
        <Controller
          name="facility.timezone"
          control={control}
          render={({ name, value, ref, onChange }) => (
            <Autocomplete
              name={name}
              data-cy="facility.timezone"
              disableClearable
              required
              value={value}
              options={timezones}
              onChange={(e, nextValue) => onChange(nextValue)}
              inputRef={ref}
              disabled={preventExternalFacilityEdit}
              className={classes.facilityInput}
              classes={{
                listbox: classes.listbox,
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Time Zone"
                  variant="outlined"
                  error={!!errors.facility?.timezone}
                  helperText={errors.facility?.timezone?.message}
                />
              )}
            />
          )}
        />
      )}
      <Controller
        name="facility.phone"
        control={control}
        render={({ name, value, ref, onChange }) => (
          <TextField
            name={name}
            data-cy="facility.phone"
            label="Phone (optional)"
            variant="outlined"
            className={classes.facilityInput}
            error={!!errors.facility?.phone}
            helperText={errors.facility?.phone?.message}
            inputRef={ref}
            disabled={preventExternalFacilityEdit}
            value={value}
            onChange={onChange}
            fullWidth
          />
        )}
      />
      <Controller
        name="facility.url"
        control={control}
        render={({ name, value, ref, onChange }) => (
          <TextField
            name={name}
            data-cy="facility.url"
            label="Website (optional)"
            variant="outlined"
            className={classes.facilityInput}
            error={!!errors.facility?.url}
            helperText={
              errors.facility?.url?.message ||
              'Enter the full address (https://examplegolfcourse.com)'
            }
            inputRef={ref}
            disabled={preventExternalFacilityEdit}
            value={value}
            onChange={onChange}
            fullWidth
          />
        )}
      />
    </>
  )
}

FacilityInformationFields.propTypes = {
  classes: PropTypes.object.isRequired,
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  facility: PropTypes.object,
  facilityPersisted: PropTypes.bool,
  preventExternalFacilityEdit: PropTypes.bool,
  setFacility: PropTypes.func.isRequired,
  clearSelectedFacility: PropTypes.func.isRequired,
}

export default FacilityInformationFields
